import { useCallback, useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { Modal } from '../../../../components/Modal';
import { Skeleton } from '../../../../components/Skeleton';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { Select } from '../../../../components/inputs/Select';
import { ApiError } from '../../../../lib/apiError';
import { casesApi } from '../casesApi';
import {
  ALL_FLAGS,
  FLAG,
  hasFlag,
  normaliseFlags,
  type CaseMemberSummary,
  type CaseMemberOption,
} from '../caseTypes';

/** The matrix's columns — Repository's share dialog, word for word. */
const COLUMNS = [
  { flag: FLAG.view, label: 'View' },
  { flag: FLAG.share, label: 'Share' },
  { flag: FLAG.upload, label: 'Upload' },
  { flag: FLAG.edit, label: 'Edit' },
  { flag: FLAG.delete, label: 'Delete' },
] as const;

interface DraftRow {
  userId: string;
  displayName: string;
  email: string | null;
  employeeNumber: string;
  permissions: number;
}

/**
 * Who a case is shared with, and what each may do — a MATRIX that SAVES, the shape of
 * Repository's `<ShareModal>` with the case as the item.
 *
 * <b>Everybody offered is an EMPLOYEE.</b> The picker lists only accounts linked to a live
 * personnel record, and the server refuses anybody else on save — the picker is the convenience,
 * the server is the rule.
 *
 * <b>Save REPLACES the people table in one transaction</b>, so removing somebody is a row leaving
 * the table; the server revokes by flag and a re-grant reactivates the same row. The creator
 * leads, every box ticked and none changeable, and is never sent.
 *
 * No Autogenerate: the form picks real accounts to hand access to a case, and a generated
 * grant would give a real person access to a case nobody decided to show them.
 */
export function CaseMembersModal({
  caseId,
  caseNumber,
  onClose,
  onSaved,
}: {
  caseId: string;
  caseNumber: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [summary, setSummary] = useState<CaseMemberSummary | null>(null);
  const [draft, setDraft] = useState<DraftRow[]>([]);
  const [options, setOptions] = useState<CaseMemberOption[]>([]);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setLoadError(null);

      try {
        const next = await casesApi.members(caseId, signal);
        if (signal?.aborted) return;

        setSummary(next);
        setDraft(
          next.members.map((m) => ({
            userId: m.userId,
            displayName: m.displayName,
            email: m.email ?? null,
            employeeNumber: m.employeeNumber,
            permissions: m.permissions,
          })),
        );

        // The account list is fetched only for somebody who may use it, and fails SOFT.
        if (next.canManage) {
          casesApi
            .memberOptions(caseId, signal)
            .then((list) => {
              if (!signal?.aborted) setOptions(list);
            })
            .catch(() => undefined);
        }
      } catch (caught) {
        if (signal?.aborted) return;
        setLoadError(caught instanceof ApiError ? caught : new ApiError('Could not load who this case is shared with.', 0));
      }
    },
    [caseId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const canManage = summary?.canManage ?? false;
  const isCreator = summary != null && summary.creatorUserId === summary.callerUserId;

  function setFlag(userId: string, flag: number, on: boolean) {
    setDraft((rows) =>
      rows.map((row) => {
        if (row.userId !== userId) return row;

        // Unticking View clears everything — nothing else means anything without it.
        const next = flag === FLAG.view && !on ? 0 : on ? row.permissions | flag : row.permissions & ~flag;

        return { ...row, permissions: normaliseFlags(next) };
      }),
    );
  }

  function add(userId: string) {
    const option = options.find((o) => o.userId === userId);
    if (!option) return;

    setDraft((rows) => [
      ...rows,
      {
        userId: option.userId,
        displayName: option.displayName,
        email: option.email ?? null,
        employeeNumber: option.employeeNumber,
        // View only, to start. Anything more is a decision the reader ticks.
        permissions: FLAG.view,
      },
    ]);
  }

  async function save() {
    if (!canManage || !confirmed || saving) return;

    setSaving(true);
    setError(null);

    try {
      // A row ticked to nothing is left out — the server would leave it out too.
      await casesApi.saveMembers(
        caseId,
        draft.filter((r) => r.permissions !== 0).map((r) => ({ userId: r.userId, permissions: r.permissions })),
      );
      onSaved();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not save who this case is shared with.', 0));
    } finally {
      setSaving(false);
    }
  }

  const taken = new Set([...draft.map((r) => r.userId), summary?.creatorUserId ?? '']);

  return (
    <Modal
      open
      onClose={onClose}
      title={`Share ${caseNumber}`}
      size="xl"
      id="case-members-modal"
      confirm={canManage ? { id: 'case-members-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving } : undefined}
      footer={
        <>
          <Button id="case-members-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            {canManage ? 'Cancel' : 'Done'}
          </Button>
          {canManage ? (
            <Button
              id="case-members-submit"
              variant="primary"
              onClick={() => void save()}
              disabled={!confirmed || saving}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          ) : null}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {loadError ? (
          <ErrorState message={loadError.message} correlationId={loadError.correlationId} onRetry={() => void load()} />
        ) : summary === null ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : (
          <>
            {canManage ? (
              <Select
                id="case-members-add"
                label="Add an employee"
                description="Only accounts linked to an employee's personnel record can be given access to a case."
                placeholder="Choose an employee"
                value={null}
                onChange={add}
                disabled={saving}
                remote
                options={options
                  .filter((o) => !taken.has(o.userId))
                  .map((o) => ({
                    value: o.userId,
                    label: o.displayName,
                    sublabel: o.email ?? undefined,
                    right: <span className="font-mono text-xs text-slate-500">{o.employeeNumber}</span>,
                    keywords: `${o.employeeNumber} ${o.email ?? ''}`,
                  }))}
              />
            ) : null}

            <div className="modern-scrollbar overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full table-auto">
                <thead className="bg-slate-100">
                  <tr className="text-[9px] uppercase tracking-wider text-gray-600 [&>th]:font-normal">
                    <th className="px-3 py-2 text-left">Employee</th>
                    {COLUMNS.map((column) => (
                      <th key={column.flag} className="border-l border-slate-200 px-3 py-2 text-center">
                        {column.label}
                      </th>
                    ))}
                    <th className="border-l border-slate-200 px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.creatorUserId != null ? (
                    <MatrixRow
                      userId={summary.creatorUserId}
                      name={summary.creatorName ?? 'The case creator'}
                      email={null}
                      employeeNumber={null}
                      permissions={ALL_FLAGS}
                      locked
                      note="Created the case"
                    />
                  ) : null}

                  {draft.map((row) => {
                    /*
                      The server's own rule, drawn: a non-creator grants only flags they hold,
                      and never touches their own row or one holding more than they do.
                    */
                    const isSelf = row.userId === summary.callerUserId;
                    const stronger = !isCreator && (row.permissions & ~summary.callerPermissions) !== 0;
                    const rowLocked = !canManage || saving || (!isCreator && (isSelf || stronger));

                    return (
                      <MatrixRow
                        key={row.userId}
                        userId={row.userId}
                        name={row.displayName}
                        email={row.email}
                        employeeNumber={row.employeeNumber}
                        permissions={row.permissions}
                        locked={rowLocked}
                        grantable={isCreator ? ALL_FLAGS : summary.callerPermissions}
                        note={!isCreator && isSelf ? 'You' : !isCreator && stronger ? 'Holds more than you' : undefined}
                        onToggle={(flag, on) => setFlag(row.userId, flag, on)}
                        onRemove={canManage && !rowLocked ? () => setDraft((rows) => rows.filter((r) => r.userId !== row.userId)) : undefined}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* What a grant cannot do, said out loud — the second key a share never hands over. */}
            <p className="text-xs text-slate-500">
              These boxes decide what each person may do in this case. They also need the
              matching permissions in each module — Cases, Evidence — and a clearance at or above
              the case&apos;s access level, to use them.
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}

function MatrixRow({
  userId,
  name,
  email,
  employeeNumber,
  permissions,
  locked,
  grantable = ALL_FLAGS,
  note,
  onToggle,
  onRemove,
}: {
  userId: string;
  name: string;
  email: string | null;
  employeeNumber: string | null;
  permissions: number;
  locked?: boolean;
  /** The flags the CALLER may hand over. A box outside it is disabled — the save would refuse it. */
  grantable?: number;
  note?: string;
  onToggle?: (flag: number, on: boolean) => void;
  onRemove?: () => void;
}) {
  return (
    <tr className="border-t border-slate-200">
      <td className="px-3 py-2 align-middle">
        <p className="text-sm text-slate-800">{name}</p>
        {/* Suppressed where it IS the name — an account with no alias falls back to its email. */}
        {email && email !== name ? <p className="text-xs text-slate-500">{email}</p> : null}
        {employeeNumber ? <p className="font-mono text-xs text-slate-500">{employeeNumber}</p> : null}
      </td>

      {COLUMNS.map((column) => (
        <td key={column.flag} className="border-l border-slate-200 px-3 py-2 text-center align-middle">
          <Checkbox
            id={`case-member-${userId}-${column.flag}`}
            aria-label={`${column.label} for ${name}`}
            checked={hasFlag(permissions, column.flag)}
            onChange={(e) => onToggle?.(column.flag, e.target.checked)}
            disabled={locked || !hasFlag(grantable, column.flag)}
          />
        </td>
      ))}

      <td className="border-l border-slate-200 px-3 py-2 text-right align-middle">
        {note ? <span className="text-xs text-slate-500">{note}</span> : null}
        {onRemove ? (
          <button type="button" className="text-xs font-medium text-red-600 hover:underline" onClick={onRemove}>
            Remove
          </button>
        ) : null}
      </td>
    </tr>
  );
}
